import { useMemo, useState } from 'react';
import { cn } from '@/lib/utils';
import { getBookSectionColor, type BibleBook, BIBLE_BOOKS, normalizeBookName, calculateBiblePositionPercent } from '@/lib/utils/bible-utils';
import { parseRef } from '@/lib/utils/reference-utils';
import { useDarkMode } from '@/lib/hooks/use-dark-mode';
import { getOutlinesByBook } from '@/data/outline/outline-data-service';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { findPassageIdForRef } from './literary-context-utils';
import { getSectionBackgroundColor } from '@/lib/utils/scripture-section-colors';

interface HorizontalContextNavigatorProps {
    mode: 'book' | 'bible';
    passage: Passage;
    allPassages: Passage[];
    outlineData?: Outline[];
    setUrl: (url: string) => void;
    className?: string;
    sticky?: boolean;
    connectionPoints?: Array<{ id: string; ref: string; percent: number; type: 'allusion' | 'crossref' }>;
    onMarkerClick?: (id: string) => void;
    isTransitionAnimating?: boolean;
}

interface NavSegment {
    id: string;
    label: string;
    widthPercent: number;
    color: string;
    isActive: boolean;
    onClick: () => void;
}

export function HorizontalContextNavigator({
    mode,
    passage,
    allPassages,
    outlineData,
    setUrl,
    className,
    sticky = true,
    connectionPoints = [],
    onMarkerClick,
    isTransitionAnimating
}: HorizontalContextNavigatorProps) {
    const isDarkMode = useDarkMode();
    const [hoveredSegment, setHoveredSegment] = useState<string | null>(null);

    const segments = useMemo(() => {
        if (mode === 'book') {
            if (!outlineData) return [];

            const outlinesByBook = getOutlinesByBook(outlineData);
            const bookOutlines = outlinesByBook.get(passage.book) || [];
            const bookOutline = bookOutlines[0];

            if (!bookOutline) return [];

            const bookPassages = allPassages
                .filter(p => p.book === passage.book)
                .sort((a, b) => {
                    const aRef = parseRef(a.ref);
                    const bRef = parseRef(b.ref);
                    if (!aRef || !bRef) return 0;
                    if (aRef.chapterStart !== bRef.chapterStart) {
                        return aRef.chapterStart - bRef.chapterStart;
                    }
                    return aRef.verseStart - bRef.verseStart;
                });

            let totalVerses = 0;
            const segmentsWithVerses = bookPassages.map(p => {
                const findNode = (nodes: OutlineNode[]): OutlineNode | null => {
                    for (const node of nodes) {
                        if (node.sectionIds.includes(p.id)) return node;
                        if (node.children) {
                            const found = findNode(node.children);
                            if (found) return found;
                        }
                    }
                    return null;
                };
                const node = findNode(bookOutline.structure);
                const verses = node?.numVerses || 1;
                totalVerses += verses;
                return { passage: p, verses };
            });

            // Extract the base color
            const sectionColorClass = getBookSectionColor(passage.book as BibleBook);
            const colorMatch = sectionColorClass.match(/border-l-(\w+)-/);
            const colorName = colorMatch ? colorMatch[1] : 'blue';

            // Simplified HSL lookup logic similar to BookPositionChart
            const getColor = (isCurrent: boolean) => {
                const colorMap: Record<string, string> = {
                    blue: '217, 91%, 60%',
                    green: '142, 71%, 45%',
                    purple: '270, 91%, 65%',
                    indigo: '239, 84%, 67%',
                    teal: '173, 80%, 40%',
                    red: '0, 84%, 60%',
                    orange: '27, 96%, 61%',
                    amber: '38, 92%, 50%',
                    yellow: '48, 96%, 53%',
                    gray: '0, 0%, 60%'
                };
                const base = colorMap[colorName] || colorMap.gray;
                return `hsla(${base}, ${isCurrent ? 1 : 0.3})`;
            };

            return segmentsWithVerses.map(s => ({
                id: s.passage.id,
                label: s.passage.title || s.passage.ref,
                widthPercent: (s.verses / totalVerses) * 100,
                color: getColor(s.passage.id === passage.id),
                isActive: s.passage.id === passage.id,
                onClick: () => setUrl(`/app/passage/${s.passage.id}/context/book`)
            }));
        } else {
            // mode === 'bible'
            const totalChapters = BIBLE_BOOKS.reduce((sum, b) => sum + b.numChapters, 0);

            return BIBLE_BOOKS.map(book => {
                const isCurrentBook = book.name === passage.book;
                const backgroundColor = getSectionBackgroundColor(book.section, isDarkMode, isCurrentBook);

                return {
                    id: book.name,
                    label: normalizeBookName(book.name),
                    widthPercent: (book.numChapters / totalChapters) * 100,
                    color: backgroundColor,
                    isActive: isCurrentBook,
                    onClick: () => {
                        const passageId = findPassageIdForRef(book.name, '1', allPassages);
                        if (passageId) {
                            setUrl(`/app/passage/${passageId}/context/scripture`);
                        } else {
                            setUrl(`/app/read/${book.name}/1`);
                        }
                    }
                };
            });
        }
    }, [mode, passage, allPassages, outlineData, setUrl, isDarkMode]);

    const activePositionPercent = useMemo(() => {
        // High-resolution arrow positioning
        if (mode === 'bible' && passage) {
            const range = parseRef(passage.ref);
            const startChapter = range?.chapterStart || 1;
            return calculateBiblePositionPercent(passage.book as BibleBook, startChapter);
        }

        if (mode === 'book' && passage) {
            // For book mode, calculate precise position based on verse offset
            // Fallback to segment center if we can't determine the exact point
            const activeSegment = segments.find(s => s.isActive);
            if (!activeSegment) return 0;
            const index = segments.indexOf(activeSegment);
            return segments.slice(0, index).reduce((sum, s) => sum + s.widthPercent, 0) + (activeSegment.widthPercent / 2);
        }

        const activeSegment = segments.find(s => s.isActive);
        if (!activeSegment) return 0;
        const index = segments.indexOf(activeSegment);
        return segments.slice(0, index).reduce((sum, s) => sum + s.widthPercent, 0) + (activeSegment.widthPercent / 2);
    }, [mode, segments, passage]);

    const clusteredMarkers = useMemo(() => {
        const THRESHOLD = 3; // percent
        const sorted = [...connectionPoints].sort((a, b) => a.percent - b.percent);

        return sorted.map((marker, i) => {
            // Cross-references always sit at the base level and don't stack
            if (marker.type === 'crossref') {
                return { ...marker, offsetY: 0 };
            }

            // Allusions stack with other allusions to stay distinguishable
            let offset = 0;
            for (let j = i - 1; j >= 0; j--) {
                const prev = sorted[j];
                // Check if we're still within the threshold
                if (marker.percent - prev.percent < THRESHOLD) {
                    // Only stack against other allusions
                    if (prev.type === 'allusion') {
                        offset++;
                    }
                } else {
                    // Sorted list, so we can stop looking
                    break;
                }
            }
            return { ...marker, offsetY: offset * -18 }; // Stacking height
        });
    }, [connectionPoints]);

    return (
        <div className={cn(
            "w-full px-2 sm:px-4 transition-all duration-300",
            sticky ? "sticky top-0 z-40 pt-10 pb-2.5 bg-background/95 backdrop-blur-xl border-b border-border/10 shadow-sm" : "py-4",
            className
        )}>
            {mode === 'book' && (
                <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground/50 px-1 pb-1.5">
                    <span>{normalizeBookName(passage.book)}</span>
                </div>
            )}

            <div
                id="horizontal-context-navigator"
                data-target-position={activePositionPercent}
                className="relative h-2.5 w-full flex items-stretch rounded-full bg-muted/20 border border-border/10 group/nav shadow-inner"
            >
                {/* Active Indicator Arrow & Label (Now Below Bar) */}
                {segments.map((segment) => segment.isActive && (
                    <div
                        key={`arrow-${segment.id}`}
                        className="absolute top-full mt-1.5 h-[30px] z-30 transition-all duration-500 ease-in-out pointer-events-none flex flex-col items-center"
                        style={{
                            left: `${activePositionPercent}%`,
                            transform: 'translateX(-50%)'
                        }}
                    >
                        <div className="h-2 w-3 bg-foreground shadow-[0_0_8px_hsla(var(--foreground),0.2)]"
                            style={{ clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)' }} />
                        <span className="text-[9px] font-bold tracking-widest text-foreground whitespace-nowrap bg-background/50 backdrop-blur-sm px-1.5 rounded-sm mt-0.5">
                            {mode === 'book' ? passage.ref : normalizeBookName(passage.book)}
                        </span>
                    </div>
                ))}

                {/* High-Resolution Active Position Line (Synced with Arrow) */}
                {passage && (
                    <div
                        className="absolute inset-y-0 w-0.5 bg-foreground/80 shadow-[0_0_4px_hsla(var(--foreground),0.3)] z-30 transition-all duration-500 ease-in-out pointer-events-none"
                        style={{
                            left: `${activePositionPercent}%`,
                            transform: 'translateX(-50%)'
                        }}
                    />
                )}

                {/* Connection Point Markers (Numbered Circles for Allusions, Dots for Cross-Refs) */}
                {clusteredMarkers.map((marker, index) => {
                    const isAllusion = (marker as any).type === 'allusion';
                    const isCrossRef = (marker as any).type === 'crossref';

                    // Assign number relative to its own type if needed, but for now we follow global index
                    // Actually, the user wants to deal with "too many numbered circles".
                    // So we only number allusions.

                    const markerNumber = isAllusion
                        ? clusteredMarkers.filter((m: any, idx) => m.type === 'allusion' && idx <= index).length
                        : null;

                    return (
                        <div
                            key={marker.id}
                            data-marker-id={marker.id}
                            className="absolute z-20 transition-all duration-500"
                            style={{
                                left: `${marker.percent}%`,
                                bottom: '100%',
                                marginBottom: isAllusion ? `${marker.offsetY + 6}px` : `${marker.offsetY + 4}px`,
                                transform: 'translateX(-50%)'
                            }}
                        >
                            <TooltipProvider delayDuration={0}>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <button
                                            onClick={() => onMarkerClick?.(marker.id)}
                                            data-marker-id={marker.id}
                                            className={cn(
                                                "rounded-full flex items-center justify-center font-bold z-20 transition-all hover:scale-125 cursor-pointer",
                                                isAllusion
                                                    ? "w-4 h-4 text-[9px] shadow-lg border border-white/20 bg-primary text-primary-foreground"
                                                    : "w-1 h-1 opacity-30 bg-primary/60"
                                            )}
                                        >
                                            {markerNumber}
                                        </button>
                                    </TooltipTrigger>
                                    <TooltipContent side="top" className="text-[10px] py-1 px-2">
                                        <div className="flex flex-col gap-0.5">
                                            <span className="font-bold uppercase tracking-widest text-[8px] opacity-70">
                                                {isAllusion ? 'Allusion' : 'Cross-Reference'}
                                            </span>
                                            <span>{marker.ref}</span>
                                        </div>
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                        </div>
                    );
                })}

                <TooltipProvider delayDuration={0}>
                    {segments.map((segment) => (
                        <Tooltip key={segment.id}>
                            <TooltipTrigger asChild>
                                <div
                                    className={cn(
                                        "relative h-full transition-all duration-300 cursor-pointer first:rounded-l-full last:rounded-r-full border-r last:border-r-0 border-white/5",
                                        segment.isActive ? "z-10 overflow-visible" : "overflow-hidden hover:brightness-125 hover:z-20"
                                    )}
                                    style={{
                                        width: `${segment.widthPercent}%`,
                                        backgroundColor: segment.color,
                                    }}
                                    onClick={segment.onClick}
                                    onMouseEnter={() => setHoveredSegment(segment.id)}
                                    onMouseLeave={() => setHoveredSegment(null)}
                                >
                                    {segment.isActive && isTransitionAnimating && (
                                        <div
                                            className="absolute bg-white opacity-0 animate-throb-glow z-30 rounded-full"
                                            style={{
                                                // Extend beyond segment bounds for visibility on small segments
                                                top: '-4px',
                                                bottom: '-4px',
                                                left: '-8px',
                                                right: '-8px',
                                                minWidth: '20px',
                                            }}
                                        />
                                    )}
                                </div>
                            </TooltipTrigger>
                            <TooltipContent side="top" sideOffset={10} className="text-xs font-serif px-3 py-1.5 backdrop-blur-md bg-background/90 border-primary/20 shadow-xl">
                                <p className="font-semibold text-foreground">{segment.label}</p>
                            </TooltipContent>
                        </Tooltip>
                    ))}
                </TooltipProvider>
            </div>
        </div>
    );
}
